import { BULLION_GROUP_COUNT } from "./constants";
import { isBullionInplayOnlyMarketTypeId } from "./bullionInplayTypeIds";

/**
 * BULLION cricket board: allowed Decimal `market_type_id` values and which tab they belong to.
 *
 * TEAM — Match Winner, Toss Winner, Highest First Over, Highest First Session, Highest Opening Partnership, Most Fours, Most Sixes
 * PLAYER — prematch batter runs/sixes only (910/914). All Decimal in-play-only types excluded.
 * MATCH — Match Runs/Fours/Sixes/Wickets/Run Outs/Highest Runs off Over/Highest Individual Score
 * INNINGS — Session runs (First 6 T20 / First 10 ODI), Fall of Wicket, Innings Fours, Innings Sixes (per team)
 */
/** Allowed Decimal `market_type_id` values on the BULLION cricket board — also used for “line on selection” O/U labelling. */
export const BULLION_CRICKET_BOARD_TYPE_IDS = new Set([
    "10",
    "1010",
    "15",
    "815",
    "50",
    "55",
    "45",
    "30",
    "35",
    "510",
    "610",
    "60",
    "70",
    "75",
    "80",
    "85",
    "86",
    "88",
    "220",
    "230",
    "910",
    "914",
]);

/** Batter fours type ids (live 720, prematch 912) — never on the BULLION board. */
const BULLION_EXCLUDED_BATTER_FOURS_TYPE_IDS = new Set(["720", "912"]);

function isBullionExcludedBatterFoursMarket(market: {
    market_type_id?: string | number;
    market_type?: string;
    market_name?: string;
    name?: string;
}): boolean {
    const typeId = normalizeTypeId(market.market_type_id);
    if (typeId && BULLION_EXCLUDED_BATTER_FOURS_TYPE_IDS.has(typeId)) return true;
    const t = marketTypeText(market);
    if (/\bpre\s*match\s+batsman\s+fours?\b/i.test(t)) return true;
    if (
        (/\bbatsman\s+fours?\b/i.test(t) || /\bbatter\s+fours?\b/i.test(t)) &&
        !t.includes("innings fours") &&
        !t.includes("match fours") &&
        !t.includes("most fours")
    ) {
        return true;
    }
    return false;
}

const BULLION_TYPE_ID_TO_GROUP: Record<string, number> = {
    "10": 0,
    "1010": 0,
    "15": 0,
    "815": 0,
    "50": 0,
    "55": 0,
    "45": 0,
    "30": 0,
    "35": 0,
    "910": 1,
    "914": 1,
    "60": 2,
    "70": 2,
    "75": 2,
    "80": 2,
    "85": 2,
    "88": 2,
    "86": 2,
    "220": 3,
    "230": 3,
    "510": 3,
    "610": 3,
};

function normalizeTypeId(raw: unknown): string {
    const s = String(raw ?? "").trim();
    if (!s) return "";
    if (/^\d+(\.0+)?$/.test(s)) return String(parseInt(s, 10));
    return s;
}

function isMatchWinnerTypeId(typeId: string): boolean {
    return typeId === "10" || typeId === "1010" || typeId === "15";
}

function marketTypeText(market: {
    market_type?: string;
    market_name?: string;
    name?: string;
    market_group?: string;
}): string {
    return String(market.market_type || market.market_name || market.name || "").toLowerCase();
}

function marketGroupText(market: { market_group?: string }): string {
    return String(market.market_group || "").toLowerCase();
}

function isBullionAllowedCricketTypeText(typeText: string): boolean {
    return (
        typeText.includes("match winner") ||
        typeText.includes("toss winner") ||
        typeText.includes("highest first over") ||
        typeText.includes("highest first session") ||
        typeText.includes("highest opening partnership") ||
        typeText.includes("most fours") ||
        typeText.includes("most sixes") ||
        typeText.includes("match runs") ||
        typeText.includes("runs in match") ||
        typeText.includes("match fours") ||
        typeText.includes("match sixes") ||
        typeText.includes("match wickets") ||
        typeText.includes("match run outs") ||
        typeText.includes("match highest runs off over") ||
        typeText.includes("match highest individual score") ||
        typeText.includes("innings fours") ||
        typeText.includes("innings sixes") ||
        typeText.includes("total fours") ||
        typeText.includes("total sixes") ||
        typeText.includes("session runs") ||
        typeText.includes("fall of first wicket")
    );
}

export function isBullionTraderCricketMarket(
    market: {
        market_type_id?: string | number;
        market_type?: string;
        market_name?: string;
        name?: string;
        market_group?: string;
    },
    format?: number
): boolean {
    const typeId = normalizeTypeId(market.market_type_id);
    const isTest = format === 999;
    if (typeId === "15" && !isTest) return false;
    if (isBullionExcludedBatterFoursMarket(market)) return false;
    if (typeId && isBullionInplayOnlyMarketTypeId(typeId)) return false;
    if (isMatchWinnerTypeId(typeId)) return true;
    if (!typeId) {
        const typeText = marketTypeText(market);
        if (isBullionAllowedCricketTypeText(typeText)) return true;
        return false;
    }
    return BULLION_CRICKET_BOARD_TYPE_IDS.has(typeId);
}

export function getBullionCricketMarketGroupIndex(market: {
    market_type_id?: string | number;
    market_type?: string;
    market_name?: string;
    name?: string;
    market_group?: string;
}): number {
    const typeId = normalizeTypeId(market.market_type_id);
    if (typeId && BULLION_TYPE_ID_TO_GROUP[typeId] !== undefined) {
        return BULLION_TYPE_ID_TO_GROUP[typeId];
    }
    const t = marketTypeText(market);
    const g = marketGroupText(market);
    if (
        t.includes("match winner") ||
        t.includes("toss winner") ||
        t.includes("highest first over") ||
        t.includes("highest first session") ||
        t.includes("highest opening partnership") ||
        t.includes("most fours") ||
        t.includes("most sixes")
    ) return 0;
    if (!isBullionExcludedBatterFoursMarket(market) && (t.includes("batter") || t.includes("batsman") || t.includes("player")))
        return 1;
    if (
        t.includes("match runs") ||
        t.includes("runs in match") ||
        t.includes("match fours") ||
        t.includes("match sixes") ||
        t.includes("match wickets") ||
        t.includes("match run outs") ||
        t.includes("match highest runs off over") ||
        t.includes("match highest individual score")
    ) return 2;
    if (
        t.includes("innings fours") ||
        t.includes("innings sixes") ||
        t.includes("total fours") ||
        t.includes("total sixes") ||
        t.includes("session runs") ||
        t.includes("fall of first wicket")
    ) return 3;
    if (g.includes("team")) return 0;
    if (g.includes("player")) return 1;
    if (g.includes("match")) return 2;
    if (g.includes("runs")) return 2;
    if (g.includes("innings")) return 3;
    return 0;
}

/** TEAM tab: Match Winner → Toss → Highest First Over → … → Most Sixes. */
const BULLION_TEAM_TYPE_ORDER: Record<string, number> = {
    "1010": 0,
    "10": 1,
    "15": 2,
    "815": 3,
    "50": 4,
    "55": 5,
    "45": 6,
    "30": 7,
    "35": 8,
};

function displayTitle(m: {
    name?: string;
    market_name?: string;
    market_type?: string;
}): string {
    return String(m.name || m.market_name || m.market_type || "").trim();
}

/** 0 = favours home / team A, 1 = away / team B; used for MATCH + INNINGS paired lines. */
function bullionTeamSlotForTitle(title: string, teamA: string, teamB: string): number {
    const n = title.toLowerCase();
    const a = teamA.trim().toLowerCase();
    const b = teamB.trim().toLowerCase();
    if (!a || !b) return 0;
    const ia = n.indexOf(a);
    const ib = n.indexOf(b);
    const hasA = ia >= 0;
    const hasB = ib >= 0;
    if (hasA && !hasB) return 0;
    if (hasB && !hasA) return 1;
    if (hasA && hasB) return ia <= ib ? 0 : 1;
    return 0;
}

/** 1–4 when the title names a batter slot; large when unknown (stable tie-break by name). */
function bullionBatterOrdinalFromTitle(title: string): number {
    const m = title.match(/batter\s*(\d+)|batsman\s*(\d+)|player\s*#?\s*(\d+)/i);
    if (m) {
        const x = parseInt(m[1] || m[2] || m[3], 10);
        if (Number.isFinite(x) && x >= 1 && x <= 99) return x;
    }
    return 500;
}

/**
 * Sort key within one BULLION group (TEAM / PLAYER / MATCH / INNINGS) — product order from spec.
 * Tie-break with `displayTitle` in the comparator.
 */
function bullionCricketInGroupOrderKey(
    market: { market_type_id?: string | number; market_type?: string; name?: string; market_name?: string },
    teamA: string,
    teamB: string
): number {
    const typeId = normalizeTypeId(market.market_type_id);
    const title = displayTitle(market);
    const g = getBullionCricketMarketGroupIndex(market);

    if (g === 0) {
        if (typeId && BULLION_TEAM_TYPE_ORDER[typeId] !== undefined) return BULLION_TEAM_TYPE_ORDER[typeId] * 10;
        const t = String(market.market_type || "").toLowerCase();
        if (t.includes("match winner")) return BULLION_TEAM_TYPE_ORDER["1010"] * 10;
        return 9990;
    }

    const slot = bullionTeamSlotForTitle(title, teamA, teamB);

    if (g === 1) {
        const ord = bullionBatterOrdinalFromTitle(title);
        if (typeId === "910") return 0 + ord;
        if (typeId === "914") return 4000 + ord;
        return 9000;
    }

    if (g === 2) {
        if (typeId === "60") return 0;
        if (typeId === "70") return 100;
        if (typeId === "75") return 200;
        if (typeId === "85") return 300;
        if (typeId === "80") return 400;
        if (typeId === "88") return 500;
        if (typeId === "86") return 600;
        return 9000;
    }

    if (g === 3) {
        if (typeId === "510") return 0 + slot * 100;
        if (typeId === "610") return 200 + slot * 100;
        if (typeId === "220") return 400 + slot * 100;
        if (typeId === "230") return 600 + slot * 100;
        return 9000;
    }

    return 9999;
}

/** Full-board sort: group order TEAM → PLAYER → MATCH → INNINGS, then spec order inside each tab. */
export function compareBullionCricketRawMarketsForOrdering(
    a: { market_type_id?: string | number; market_type?: string; name?: string; market_name?: string },
    b: { market_type_id?: string | number; market_type?: string; name?: string; market_name?: string },
    teamA: string,
    teamB: string
): number {
    const ga = getBullionCricketMarketGroupIndex(a);
    const gb = getBullionCricketMarketGroupIndex(b);
    if (ga !== gb) return ga - gb;
    const ka = bullionCricketInGroupOrderKey(a, teamA, teamB);
    const kb = bullionCricketInGroupOrderKey(b, teamA, teamB);
    if (ka !== kb) return ka - kb;
    return displayTitle(a).localeCompare(displayTitle(b), undefined, { sensitivity: "base" });
}

function slugKeyPart(raw: string): string {
    return String(raw || "")
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9_-]/g, "")
        .slice(0, 120);
}

/**
 * Stable row id for BULLION / dedupe. When `market_line_id` is missing, many lines share the same `market_id`;
 * we fold in type id + name so each offerable line stays a distinct card (same idea as Trader line keys).
 */
export function bullionMarketStableId(
    market: {
        market_id?: string | number;
        id?: string | number;
        market_line_id?: string | number;
        market_type_id?: string | number;
        name?: string;
        market_name?: string;
        market_type?: string;
    },
    index: number
): string {
    const mid = market.market_id != null ? String(market.market_id) : market.id != null ? String(market.id) : "";
    const lineAlt = (market as { line?: string | number }).line;
    const lid =
        market.market_line_id != null && String(market.market_line_id) !== ""
            ? String(market.market_line_id)
            : lineAlt != null && String(lineAlt) !== ""
              ? String(lineAlt)
              : "";
    const tid = String(market.market_type_id ?? "");
    const nameKey = slugKeyPart(market.name || (market as { market_name?: string }).market_name || market.market_type || "");

    if (mid && lid) return `${mid}_${lid}`;
    if (mid) return `${mid}__${lid || "noline"}__${tid}__${nameKey || "noname"}`;
    if (tid) return `${tid}__${nameKey || "noname"}`;
    return `m-${index}__${nameKey || "unnamed"}`;
}

export function clampBullionGroupIndex(g: number): number {
    if (!Number.isFinite(g)) return 0;
    return Math.min(BULLION_GROUP_COUNT - 1, Math.max(0, g));
}
